import { Badge, Button, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import type { LiveLock, LiveLockFlowStep, LiveLockInstance } from 'api/locks/endpoints/getLiveLocks';
import { Columns } from 'components/base/Columns';
import StatusBadge from 'components/base/StatusBadge';
import { DetailCard } from 'components/details/DetailCard';
import FormatDateTime from 'components/common/FormatDateTime';
import { NodeInstanceLink, nodeInstanceLabel } from 'pages/apps/platform/list_node_instances/NodeInstanceLink';

import type { LiveLocksModel } from './LiveLocksModel';
import './LiveLockCard.scss';

interface LiveLockCardProps {
  readonly lock: LiveLock;
  readonly model: LiveLocksModel;
  /**
   * Focus is owned by the URL, so pinning is the page's to perform. Keep it
   * stable: the card is memoised, and a new function each render re-renders
   * every card.
   */
  readonly onTogglePin: (lockId: number) => void;
}

/** Milliseconds from `since` to `asOf`, or null when either is missing or unreadable. */
const elapsedMs = (since: string | null, asOf: string | null): number | null => {
  if (since === null || asOf === null) {
    return null;
  }
  const ms = Date.parse(asOf) - Date.parse(since);
  return Number.isNaN(ms) ? null : Math.max(0, ms);
};

/**
 * A row's React key. A redacted instance has no id, and several can share a
 * list, so it is keyed by position; the prefixes keep the two kinds from
 * colliding.
 */
export const instanceRowKey = (instance: LiveLockInstance, index: number): string =>
  instance.redacted ? `redacted-${index}` : `instance-${instance.node_instance_id}`;

/** The instance a row links to as its root, or null when it is not known. */
const rootOf = (flowPath: readonly LiveLockFlowStep[], nodeInstanceId: number): number | null => {
  // Outermost first, so the head is the root; empty means top-level. A hidden
  // root cannot be opened, so the link falls back to the instance itself.
  const head = flowPath[0];
  if (head === undefined) {
    return nodeInstanceId;
  }
  return head.redacted ? null : head.node_instance_id;
};

const HIDDEN_TITLE = 'In a zone whose node instances you may not list';

/** The flows an instance runs inside, so a child step reads as part of its flow. */
const FlowPath = ({ steps }: { readonly steps: readonly LiveLockFlowStep[] }) => (
  <>
    {steps.map((step, index) =>
      step.redacted ? (
        <span key={`redacted-${index}`} className="text-muted fst-italic" title={HIDDEN_TITLE}>
          Hidden flow ›{' '}
        </span>
      ) : (
        <span key={step.node_instance_id} className="text-muted">
          {nodeInstanceLabel(step.node_instance_id, step.node_name)} ›{' '}
        </span>
      )
    )}
  </>
);

interface InstanceRowProps {
  readonly instance: LiveLockInstance;
  /** When the page's answer was read; durations are measured to it, so they agree with the rest of the card. */
  readonly asOf: string | null;
}

const InstanceRow = ({ instance, asOf }: InstanceRowProps) => {
  const elapsed = elapsedMs(instance.since, asOf);
  return (
    <tr>
      <td className="live-lock-card__name-cell">
        {instance.redacted ? (
          <span className="text-muted fst-italic" title={HIDDEN_TITLE}>
            Hidden instance
          </span>
        ) : (
          <>
            <FlowPath steps={instance.flow_path} />
            <NodeInstanceLink
              nodeInstanceId={instance.node_instance_id}
              nodeName={instance.node_name}
              rootNodeInstanceId={rootOf(instance.flow_path, instance.node_instance_id)}
            />
          </>
        )}
      </td>
      <td>
        <StatusBadge status={instance.status} />
      </td>
      <td>{instance.since === null ? '—' : <FormatDateTime value={instance.since} />}</td>
      <td>{elapsed === null ? '—' : <FormatDateTime value={elapsed} variant="duration" />}</td>
    </tr>
  );
};

interface InstanceListProps {
  readonly instances: readonly LiveLockInstance[];
  /** The true count, which the list may fall short of. */
  readonly count: number;
  readonly sinceHeading: string;
  readonly empty: string;
  readonly asOf: string | null;
}

const InstanceList = ({ instances, count, sinceHeading, empty, asOf }: InstanceListProps) => {
  if (count === 0 && instances.length === 0) {
    return <p className="text-muted mb-0">{empty}</p>;
  }
  return (
    <>
      {/* No table at all when every row was lost: headers over nothing read as a bug. */}
      {instances.length > 0 && (
        <Table size="sm" className="live-lock-card__table mb-0">
          <thead>
            <tr>
              <th>Instance</th>
              <th>Status</th>
              <th>{sinceHeading}</th>
              <th>For</th>
            </tr>
          </thead>
          <tbody>
            {instances.map((instance, index) => (
              <InstanceRow key={instanceRowKey(instance, index)} instance={instance} asOf={asOf} />
            ))}
          </tbody>
        </Table>
      )}
      {/* The count is the truth; the list can be short of it, whether the
          backend capped it or a row did not survive validation. Say so, rather
          than let the list read as the whole of it. */}
      {instances.length < count && (
        <p className={instances.length > 0 ? 'text-muted small mb-0 mt-2' : 'text-muted small mb-0'}>
          Showing {instances.length} of {count}.
        </p>
      )}
    </>
  );
};

/**
 * One lock's card: who holds it on the left, who it is blocking on the right.
 *
 * An observer of its own, reading its collapse and focus state from the model,
 * so a toggle re-renders this card only and a filter keystroke none of them:
 * `observer` memoises, and `lock` keeps its identity until the next answer.
 */
export const LiveLockCard = observer(({ lock, model, onTogglePin }: LiveLockCardProps) => {
  const focused = lock.lock_id === model.focusLockId;
  // A limit lowered under running work leaves more holders than spots: full, with none free.
  const isFull = lock.held_count >= lock.limit;
  const free = isFull ? 0 : lock.limit - lock.held_count;
  const pinLabel = focused ? 'Unpin this lock' : 'Pin this lock to the top';

  const title = (
    <span className="live-lock-card__title">
      <Link to={`/configuration/locks?id=${lock.lock_id}`}>{lock.name}</Link>
      <span className="text-muted live-lock-card__zone">{lock.zone_name}</span>
      {focused && (
        <Badge bg="secondary" className="live-lock-card__badge">
          Focused
        </Badge>
      )}
      {isFull && (
        <Badge bg="warning" className="live-lock-card__badge">
          Full
        </Badge>
      )}
    </span>
  );

  const pin = (
    <Button
      variant="link"
      className={focused ? 'live-lock-card__icon-button live-lock-card__icon-button--active' : 'live-lock-card__icon-button'}
      title={pinLabel}
      aria-label={pinLabel}
      aria-pressed={focused}
      onClick={() => onTogglePin(lock.lock_id)}
    >
      <FontAwesomeIcon icon={faThumbtack} />
    </Button>
  );

  return (
    <DetailCard
      title={title}
      subtitle={`${lock.held_count} of ${lock.limit} held · ${free} free · ${lock.blocked_count} blocked`}
      collapsible
      collapsed={model.isCollapsed(lock.lock_id)}
      onToggleCollapsed={() => model.toggleCollapsed(lock.lock_id)}
      actions={pin}
    >
      <Columns>
        <Columns.Column heading="Holding" subtitle="Node instances running with this lock.">
          <InstanceList instances={lock.holders} count={lock.held_count} sinceHeading="Holding since" empty="Nothing holds this lock." asOf={model.asOf} />
        </Columns.Column>

        <Columns.Column heading="Blocked" subtitle="Node instances waiting because this lock is full.">
          <InstanceList
            instances={lock.blocked}
            count={lock.blocked_count}
            sinceHeading="Waiting since"
            empty="Nothing is waiting on this lock."
            asOf={model.asOf}
          />
        </Columns.Column>
      </Columns>
    </DetailCard>
  );
});
